/**
 * Altixo — produit offert ajouté automatiquement au panier.
 *
 * Réglages : Paramètres du thème → Offre Altixo (collection concernée + produit offert).
 * - Quand un produit de la collection est AJOUTÉ au panier, le produit offert est ajouté
 *   (une unité par produit concerné), avec la propriété cachée « _ax_cadeau ».
 * - Quand un produit concerné est retiré, les unités offertes en trop sont retirées
 *   (seulement celles ajoutées automatiquement : un achat volontaire n'est jamais touché).
 * - Si le client retire lui-même le cadeau, il n'est pas ré-ajouté tant qu'il n'ajoute pas
 *   une nouvelle échelle.
 * La gratuité elle-même vient de la réduction automatique Shopify (Achetez X, obtenez Y).
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const configElement = document.getElementById('ax-offer-config');
const config = configElement ? JSON.parse(configElement.textContent || '{}') : null;
const SOURCE = 'ax-gift';
let running = Promise.resolve();

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => element instanceof HTMLElement ? element.dataset.sectionId : '')
    .filter(Boolean);
}

async function fetchCart() {
  const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart ${response.status}`);
  return response.json();
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart error');
  return data;
}

function countLines(cart) {
  let eligible = 0;
  let autoGifts = 0;
  const giftLines = [];
  for (const item of cart.items || []) {
    if (config.eligible.includes(item.product_id)) eligible += item.quantity;
    if (item.variant_id === config.gift && item.properties && item.properties._ax_cadeau) {
      autoGifts += item.quantity;
      giftLines.push(item);
    }
  }
  return { eligible, autoGifts, giftLines };
}

/** Informe le thème (tiroir panier, pastille) que le panier a changé. */
function announce(action, lines, response, cart) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      sections: response.sections || undefined,
      items: cart.items,
      itemCount: cart.item_count,
      source: SOURCE,
      didError: false,
    },
  });
}

async function sync(allowAdd) {
  const cart = await fetchCart();
  const { eligible, autoGifts, giftLines } = countLines(cart);
  const sections = cartSectionIds().join(',');
  const sectionsUrl = window.location.pathname;

  if (allowAdd && eligible > autoGifts) {
    const quantity = eligible - autoGifts;
    const response = await postJson(`${window.Theme?.routes?.cart_add_url || '/cart/add.js'}`, {
      items: [{ id: config.gift, quantity, properties: { _ax_cadeau: '1' } }],
      sections,
      sections_url: sectionsUrl,
    });
    announce('add', [{ merchandiseId: config.gift, quantity }], response, await fetchCart());
    return;
  }

  if (autoGifts > eligible && giftLines.length) {
    let excess = autoGifts - eligible;
    for (const line of giftLines) {
      if (excess <= 0) break;
      const newQuantity = Math.max(0, line.quantity - excess);
      excess -= line.quantity - newQuantity;
      const response = await postJson('/cart/change.js', {
        id: line.key,
        quantity: newQuantity,
        sections,
        sections_url: sectionsUrl,
      });
      announce(newQuantity === 0 ? 'remove' : 'update', [{ id: line.key, quantity: newQuantity }], response, response);
    }
  }
}

function queue(allowAdd) {
  running = running
    .then(() => sync(allowAdd))
    .catch((error) => console.warn('[ax-gift]', error));
}

if (config && config.gift && Array.isArray(config.eligible) && config.eligible.length) {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const updateEvent = /** @type {any} */ (event);
    if (!updateEvent.promise) return;
    updateEvent.promise
      .then((result) => {
        if (result?.detail?.source === SOURCE) return;
        queue(updateEvent.action === 'add');
      })
      .catch(() => {});
  });
}